import { Language } from "@prisma/client";
import { DateTime } from "luxon";

export const MARBLE_WITH_TEXT_LOGO_URL =
  "https://app.marblehealth.com/MarbleHealth_Logo.png";

/**
 * Marble's operating timezone for business boundaries: pay periods, SLAs,
 * business-day math, auto-responder hours, month/OOO boundaries.
 *
 * This used to have two siblings, `DEFAULT_TZ` and `EASTERN_TZ`, both the same
 * string. They were the last hidden Eastern default: passing one satisfied the
 * formatters' required `timezone` and read as a decision at the call site
 * while still meaning "Eastern for everyone", and being named constants they
 * were invisible to `marble/no-hardcoded-timezone`. Whose clock a time belongs
 * to now has to be said out loud — see
 * `packages/core/src/modules/timezones/AGENTS.md`.
 *
 * Defined in `@marble/core` because the business-day helpers that anchor task
 * SLAs live there and run from crons as well as the app; re-exported here so
 * the app keeps reaching for it through `@/lib/constants`.
 */
export { COMPANY_TZ } from "@marble/core/modules/timezones/constants";

export const DT_PARSER = DateTime.buildFormatParser("M/d/yyyy");
export const DT_MONTH_YEAR_PARSER = DateTime.buildFormatParser("M/yyyy");

export const AWS_REGION = "us-east-1";

const JOTFORM_BASE_URL = "https://form.jotform.com/";

export const SPANISH_INFORMATION_FORM_ID = "242245993987172";
export const SPANISH_INFORMATION_FORM_URL = `${JOTFORM_BASE_URL}${SPANISH_INFORMATION_FORM_ID}`;

export const SI_SAFETY_PLAN_FORM_ID = "243396281548163";
export const SI_SAFETY_PLAN_JOTFORM_URL = `${JOTFORM_BASE_URL}${SI_SAFETY_PLAN_FORM_ID}`;

export const INTAKE_ASSESSMENTS_JOTFORM_ID = "242976101149154";
export const INTAKE_ASSESSMENTS_JOTFORM_LINK = `${JOTFORM_BASE_URL}${INTAKE_ASSESSMENTS_JOTFORM_ID}`;

export const MEDICATION_MANAGEMENT_INTAKE_JOTFORM_ID = "252585713240152";
export const MEDICATION_MANAGEMENT_INTAKE_JOTFORM_LINK = `${JOTFORM_BASE_URL}${MEDICATION_MANAGEMENT_INTAKE_JOTFORM_ID}`;

export const MEDICATION_MANAGEMENT_FOLLOW_UP_JOTFORM_ID = "252754472430153";
export const MEDICATION_MANAGEMENT_FOLLOW_UP_JOTFORM_LINK = `${JOTFORM_BASE_URL}${MEDICATION_MANAGEMENT_FOLLOW_UP_JOTFORM_ID}`;

export const ADULT_INTAKE_ASSESSMENTS_JOTFORM_ID = "250094233679158";
export const ADULT_INTAKE_ASSESSMENTS_JOTFORM_LINK = `${JOTFORM_BASE_URL}${ADULT_INTAKE_ASSESSMENTS_JOTFORM_ID}`;

export const ASSESSMENT_JOTFORM_ID = "240045325590147";

/**
 * Enrollment form for Marble's Financial Assistance Program — one program (and
 * one form) shared by both Marble care sponsorships, insurance-payer and
 * state-uninsured alike (see `isMarbleCareSponsorship`). The Spanish variant
 * is a separately-hosted translation of the same form, so always resolve which
 * one a family gets via `getMarbleFinancialAssistanceFormUrl` rather than
 * referencing either constant directly.
 */
export const MARBLE_FINANCIAL_ASSISTANCE_FORM_ID = "262387632508059";
export const MARBLE_FINANCIAL_ASSISTANCE_FORM_URL = `${JOTFORM_BASE_URL}${MARBLE_FINANCIAL_ASSISTANCE_FORM_ID}`;

export const SPANISH_MARBLE_FINANCIAL_ASSISTANCE_FORM_ID = "262454464954163";
export const SPANISH_MARBLE_FINANCIAL_ASSISTANCE_FORM_URL = `${JOTFORM_BASE_URL}${SPANISH_MARBLE_FINANCIAL_ASSISTANCE_FORM_ID}`;

/**
 * The Financial Assistance Program enrollment form a family should get, by
 * their preferred language: Spanish speakers get the translated form, everyone
 * else the English one. Resolved at message-build time (not by the Twilio
 * translation layer, which only translates copy — a link has to be swapped
 * wholesale), so every sender that includes this form URL must go through
 * here.
 */
export const getMarbleFinancialAssistanceFormUrl = (
  language?: Language | null,
): string => {
  if (language === Language.SPANISH) {
    return SPANISH_MARBLE_FINANCIAL_ASSISTANCE_FORM_URL;
  }
  return MARBLE_FINANCIAL_ASSISTANCE_FORM_URL;
};

export const DD_RUM_APP_ID = "a343bd3b-7f36-4ec3-bdac-831f233ab32f";

export const ONE_DAY_M = 60 * 24;

export const ONE_MIN_S = 60;
export const ONE_HOUR_S = 60 * ONE_MIN_S;
export const ONE_DAY_S = 24 * ONE_HOUR_S;

export const ONE_SECOND_MS = 1000;
export const ONE_MIN_MS = 60 * ONE_SECOND_MS;
export const ONE_HOUR_MS = 60 * ONE_MIN_MS;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;

export { DEFAULT_SCHOOL_OWNER_INFO } from "@marble/core/modules/schools/constants";

export const PHONE_PARSE_OPTIONS = {
  defaultCountry: "US" as const,
  extract: false,
};

export const SYSTEM_USER_EMAIL = "system@marblehealth.com";

// TEMP: Temporary list of whitelisted therapists
export const WHITELISTED_THERAPISTS = [
  "jocelynmartinezlcsw@gmail.com",
  "maryf@mfpsychotherapist.com",
  "jenniferbaezlcsw@gmail.com",
  "yizhaosheng3@gmail.com",
  "ficcpsy@gmail.com",
  "paulineurick@gmail.com",
  "lamheadway24@gmail.com",
  "wmarquez3@verizon.net",
  "dabreu17@aol.com",
  "werglamj@gmail.com",
  "ms.jessicaw@gmail.com",
  "doralisahstjohn@gmail.com",
  "princessnshaw@gmail.com",
  "sbakercounseling@healthintakes.com",
  "gabriellaruoccolmsw@gmail.com",
  "caroline.allison.hart@gmail.com",
  "ashpbeckles@gmail.com",
  "julianaluna12@gmail.com",
  "cullenhero@aol.com",
  "brian.figueroa.003@gmail.com",
  "diamondford1@gmail.com",
  "emsch.dc@gmail.com",
  "ttmmalachi@gmail.com",
  "kiyanna.williams@outlook.com",
  "mariakoch444@gmail.com",
  "kyoko@yamapsychotherapy.com",
  "annamkons@gmail.com",
  "rachel.a.clark20@gmail.com",
  "tabithaauguste@gmail.com",
  "abirschmidt@gmail.com",
  "melodyjhchoi@gmail.com",
  "amoran318@yahoo.com",
  "jamaniblair@gmail.com",
  "tammyr6512@gmail.com",
  "ssingh@sweethomeschools.org",
  "solomonweinreblmhc@gmail.com",
  "amaniburhani@yahoo.com",
  "andreamaranosw@gmail.com",
  "delillilynne@gmail.com",
  "kathryn.fortune@gmail.com",
  "ricardojesusbermudez@gmail.com",
  "shaqueenwalcott@aol.com",
  "mandyhope50@gmail.com",
  "emmagalvintherapy@gmail.com",
  "sophiamorris1204@gmail.com",
  "natasha.saran@gmail.com",
  "tfaiella521@gmail.com",
  "cinfeliciano333@gmail.com",
  "kdclinicalconsulting@gmail.com",
  "letombianca@gmail.com",
  "dennisegarcialmhc@gmail.com",
  "julaustin456@gmail.com",
  "latorrgr@gmail.com",
  "peterandrewvasquez@gmail.com",
  "sem766@nyu.edu",
  "shelzijulia@gmail.com",
  "lberrios2008@gmail.com",
  "amwarrenlmsw@gmail.com",
  "kiah5108@gmail.com",
  "freysathom@optonline.net",
  "sonj7@hotmail.com",
  "ras12593@gmail.com",
  "ehawkins07@gmail.com",
  "barbarafeldhoff2022@u.northwestern.edu",
  "noragio14@gmail.com",
  "jsrod1213@gmail.com",
  "loretarose.therapy@gmail.com",
  "nicolefpreziosi@gmail.com",
  "kwing.lcsw@gmail.com",
  "jemimacharitable@yahoo.com",
  "jameson.c.fairchild@gmail.com",
  "amoyellehadassa@gmail.com",
  "abdelhusseiny545@gmail.com",
  "sinsinga@fordham.edu",
  "brittnyhall.lmsw@gmail.com",
  "vanessajeanpaul@gmail.com",
  "bdavenportmsw@gmail.com",
  "ange007@gmail.com",
  "carolina.lopez09@gmail.com",
  "sharisechristian@structuralsolutions.onmicrosoft.com",
  "tyrasstewart@yahoo.com",
  "mouton.hayward@gmail.com",
  "irachmin6@gmail.com",
  "stephanieyemanelmsw@gmail.com",
  "melissamarble7@gmail.com",
  "adina.golob@gmail.com",
  "uzzaman.nusrat@gmail.com",
  "daniciacampbell@gmail.com",
  "jmesserouxjr@gmail.com",
  "kdiamantis98@gmail.com",
  "york.m.campos@gmail.com",
  "mindbodyivf@gmail.com",
  "ninarem90@gmail.com",
  "ficoe45@gmail.com",
  "fernandez.lili27@icloud.com",
  "claudiacchoi@gmail.com",
  "erampersant13@gmail.com",
  "rnestor5@yahoo.com",
  "kelseyderaffele@gmail.com",
  "khadijaummi97@gmail.com",
  "sophia.salmon27@gmail.com",
  "johnp.louis91@gmail.com",
  "wsdw9@aol.com",
  "sheneaolivia21@gmail.com",
  "ashleyalewis1@gmail.com",
  "road2healingllc@gmail.com",
  "12carrion@gmail.com",
  "jehielyo@yahoo.com",
  "jchully@aol.com",
  "yanapostel1@gmail.com",
  "emmababer79@gmail.com",
  "jordanknissen@gmail.com",
  "raberta.101@gmail.com",
  "ar3589@columbia.edu",
  "abychacko1012@gmail.com",
  "therapy@marshamcdonaldnyc.com",
  "trmayerhofer@gmail.com",
  "cummings.msw@gmail.com",
  "candacejanae@gmail.com",
  "davidhalelmsw@gmail.com",
  "yael320@gmail.com",
  "catherinemedina0209@gmail.com",
  "sapphirejswitzer@gmail.com",
  "jadinetmarsh@gmail.com",
  "pietrangolaren@gmail.com",
  "trainingmh23@gmail.com",
  "tahlcsw@gmail.com",
  "karlin.walcott@gmail.com",
  "kozla26@hotmail.com",
  "chelsie@chelsiegreen.com",
  "sasharobin86@gmail.com",
  "karynsmithlcsw@gmail.com",
  "jn2211@nyu.edu",
  "sshayika@yahoo.com",
  "argenys.taveras@gmail.com",
  "ritasafyan@gmail.com",
  "jmargolis28@gmail.com",
  "lindseyheath.r@gmail.com",
  "evabada1@gmail.com",
  "mr4149@columbia.edu",
  "melis.clayton@gmail.com",
  "elaineho34@gmail.com",
  "priscillainoa6@gmail.com",
  "katiedamico444@gmail.com",
  "cgoodhall1@gmail.com",
  "brittanybaez01@gmail.com",
  "jcastro82@outlook.com",
  "chorourke93@gmail.com",
  "dlesser@ebcounseling.org",
  "tanisha.castellanos@gmail.com",
  "danimcconville@gmail.com",
  "kristinhanleyy@gmail.com",
  "leslie.friedman3@gmail.com",
  "cleolamorrisonsw@gmail.com",
  "ljohn3@fordham.edu",
  "lm4708@nyu.edu",
  "jenniferliriano.therapy@gmail.com",
  "kathydiazlcsw@gmail.com",
  "angellnuno2@gmail.com",
  "mariadougl38@gmail.com",
  "howellnm10@gmail.com",
  "sarahjpolidor@gmail.com",
  "michaela@bleeckertherapy.com",
  "paulacarrizosatherapy@gmail.com",
  "connybrs@gmail.com",
  "chanelle.lcsw@gmail.com",
  "aabreu724@gmail.com",
  "samara.k.weiss@gmail.com",
  "jessicawesternq@gmail.com",
  "shinellesingh0@gmail.com",
  "skyyeatts94@gmail.com",
  "nicoletta.aba@gmail.com",
  "marcig75@hotmail.com",
  "arielanisanov@icloud.com",
  "mvardhanlmhc@gmail.com",
  "jperalta06@gmail.com",
  "fink.abby.r@gmail.com",
  "mdiamond13@gmail.com",
  "mardayilma@gmail.com",
  "jasmerym@gmail.com",
  "aenewmanlmsw@gmail.com",
  "sheila.nazario215@gmail.com",
  "angieyemma@thedanceoftherapy.com",
  "jenbair@optonline.net",
  "kyle.lcsw@gmail.com",
  "sbpatterson@letapelinc.org",
  "ctlncrowe@gmail.com",
  "s.annesarahmarc@gmail.com",
  "rsarabialcsw@gmail.com",
  "vtmarkow@gmail.com",
  "bushra.choudhury01@gmail.com",
  "yortiz548@gmail.com",
  "kile322@gmail.com",
  "kamilah@deeplyrootedtherapy.org",
  "lakirah.robinson@gmail.com",
  "donnadolan719@gmail.com",
  "empathictouchcounseling@gmail.com",
  "younglily101@gmail.com",
  "rosemillsservices@gmail.com",
  "asaelle.mendez@gmail.com",
  "kpendermhc@gmail.com",
  "info@heartpaththerapy.com",
  "msgloria.seay@gmail.com",
  "elenalupo2014@gmail.com",
  "chrisoladuni@gmail.com",
  "gdwernick@gmail.com",
  "mycounseloratrockland@gmail.com",
  "info@mindfulcarenp.com",
  "annettekimlcsw@gmail.com",
  "luconrigaud@clmentalhealthservices.org",
  "joshburke@mail.com",
  "dynatuckertherapy@gmail.com",
  "jason.soulution@gmail.com",
  "wtbomkamp@gmail.com",
  "neliaescobartherapy@gmail.com",
  "mironwendyj@gmail.com",
  "info@brezhnevsanchez.com",
  "tomasinacook@aol.com",
  "vivmcallister01@gmail.com",
  "vdmarini@gmail.com",
  "mkushmakova18@gmail.com",
  "kbroadbelt084@yahoo.com",
  "dcohen0514@gmail.com",
  "kenglan3@gmail.com",
  "iblcswr@gmail.com",
  "smoshe11@yahoo.com",
  "harmony@headspaceharmony.com",
  "anaelengadi@gmail.com",
  "feliciahassan@yahoo.com",
  "etokareva@fordham.edu",
  "coton0228@gmail.com",
  "schauneg@gmail.com",
  "tayanaedwards@gmail.com",
  "simss1105@gmail.com",
  "rebekahfelicianolmsw@gmail.com",
  "samantha.morlack@gmail.com",
  "fabius2013@yahoo.com",
  "elenacamacho.x@gmail.com",
  "ilyssa.cohen@outlook.com",
  "zhangyuan8668@gmail.com",
  "esthermae.rosner@gmail.com",
  "nthnnthl@gmail.com",
  "cbenttaylor21@gmail.com",
  "karimarino10@verizon.net",
  "jc021184@gmail.com",
  "imarcelinnp@gmail.com",
  "cmbatista93@gmail.com",
  "kimcweeks1@aol.com",
  "sharlene@putittogetherpsychiatry.com",
  "olga.valentine@yahoo.com",
  "ortegaashley29@gmail.com",
  "storiray5@gmail.com",
  "verad13@gmail.com",
  "rpgalvinsw@gmail.com",
  "kevyntracy@outlook.com",
  "pnatanelova@gmail.com",
  "mantoine818@gmail.com",
  "amandagleichauflcsw@gmail.com",
  "williamecrabtree@gmail.com",
  "vivianpalacios@rocketmail.com",
  "irenep350@gmail.com",
  "rubanwa2000@yahoo.com",
  "crushcounseling@gmail.com",
  "arosnertherapy@gmail.com",
  "medical@hendersonhealthservices.org",
  "bsaffle16@gmail.com",
  "aortizlcsw@gmail.com",
  "ginaelston.lisws@gmail.com",
  "demarcog1398@gmail.com",
  "oem216@nyu.edu",
  "rasheedatkoleosho@yahoo.com",
  "kcashion97@gmail.com",
  "estherjung.therapist@gmail.com",
  "bmittleberg@gmail.com",
  "mbel.pena@gmail.com",
  "hauseroj@gmail.com",
  "mmorgquinn@gmail.com",
  "inhome2200@gmail.com",
  "yhernandez@swtherapist.com",
  "nadiamichel12@gmail.com",
  "bma2153@columbia.edu",
  "e.m.rodriguez89@gmail.com",
  "rainthe01@gmail.com",
  "stephaniem1687@gmail.com",
  "debiunger@hotmail.com",
  "osamwonyi.n@gmail.com",
  "nouflmsw@outlook.com",
  "juliana.nunez09@hotmail.com",
  "assumy@yahoo.com",
];
